<template>
  <div class="d-flex flex-column-fluid financing-settings-page">
    <div class="container">
      <!-- Page Header -->
      <div class="page-header mb-6">
        <div class="d-flex justify-content-between align-items-center">
          <div>
            <h1 class="title">Credit Line Settings</h1>
            <p v-if="businessName" class="subtitle">Configure financing options for {{ businessName }}</p>
          </div>
          <button class="btn btn-dark" @click="goBack">
            <i class="flaticon2-line-chart"></i>
            Back to Businesses
          </button>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="text-center py-5">
        <div class="spinner-border text-primary mb-3"></div>
        <p>Loading financing settings...</p>
      </div>

      <!-- Settings Form -->
      <div v-else>
        <!-- Fixed Alerts -->
        <div 
          v-if="error" 
          class="alert alert-danger alert-dismissible fixed-top mx-3 mt-3" 
          style="z-index: 1050; max-width: calc(100% - 2rem);"
        >
          <i class="las la-exclamation-triangle mr-2"></i>
          <strong>Error:</strong> {{ error }}
          <button type="button" class="close" @click="error = null">
            <span>&times;</span>
          </button>
        </div>

        <div 
          v-if="successMessage" 
          class="alert alert-success alert-dismissible fixed-top mx-3 mt-3" 
          style="z-index: 1050; max-width: calc(100% - 2rem);"
        >
          <i class="las la-check-circle mr-2"></i>
          <strong>Success:</strong> {{ successMessage }}
          <button type="button" class="close" @click="successMessage = null">
            <span>&times;</span>
          </button>
        </div>

        <div v-if="!hasExistingData" class="alert alert-info mb-3">
          <i class="las la-info-circle mr-2"></i>
          <strong>New Configuration:</strong> Create a new configuration below.
        </div>

        <!-- Form Layout -->
        <div class="form-sections">
          <!-- Basic Configuration -->
          <div class="form-section">
            <h3 class="section-title">
              <i class="las la-cog mr-2"></i>
              Basic Configuration
            </h3>
              <div class="row">
                <div class="col-lg-3 col-md-6">
                  <div class="form-group">
                    <label class="form-label">Credit Limit <span class="text-danger">*</span></label>
                    <div class="input-group input-group-sm">
                      <div class="input-group-prepend">
                        <span class="input-group-text">{{ formData.currency }}</span>
                      </div>
                      <input
                        type="number"
                        class="form-control form-control-sm"
                        v-model.number="formData.limit"
                        placeholder="Enter amount"
                        :disabled="saving"
                        :class="{ 'is-invalid': validationErrors.limit }"
                      />
                    </div>
                    <div v-if="validationErrors.limit" class="invalid-feedback">{{ validationErrors.limit }}</div>
                  </div>
                </div>
                <div class="col-lg-3 col-md-6">
                  <div class="form-group">
                    <label class="form-label">Settlement Fees <span class="text-danger">*</span></label>
                    <div class="input-group input-group-sm">
                      <div class="input-group-prepend">
                        <span class="input-group-text">{{ formData.currency }}</span>
                      </div>
                      <input
                        type="number"
                        class="form-control form-control-sm"
                        v-model.number="formData.settlement_fee"
                        placeholder="Enter fees"
                        :disabled="saving"
                        :class="{ 'is-invalid': validationErrors.settlement_fee }"
                      />
                    </div>
                    <div v-if="validationErrors.settlement_fee" class="invalid-feedback">{{ validationErrors.settlement_fee }}</div>
                  </div>
                </div>
                <div class="col-lg-3 col-md-6">
                  <div class="form-group">
                    <div class="d-flex justify-content-between align-items-center">
                      <label class="form-label">Onboarding Fees <span class="text-danger">*</span></label>
                      <div class="custom-switch">
                        <input
                          type="checkbox"
                          class="custom-control-input"
                          id="onboardingInvoiceToggle"
                          v-model="formData.onboarding_invoice_send"
                          :disabled="saving || hasExistingData"
                        />
                        <label class="custom-control-label" for="onboardingInvoiceToggle" style="font-size: 1rem;">
                          <small>Create Invoice</small>
                        </label>
                      </div>
                    </div>
                    <div class="input-group input-group-sm">
                      <div class="input-group-prepend">
                        <span class="input-group-text">{{ formData.currency }}</span>
                      </div>
                      <input
                        type="number"
                        class="form-control form-control-sm"
                        v-model.number="formData.onboarding_fee"
                        placeholder="Enter fees"
                        :disabled="saving"
                        :class="{ 'is-invalid': validationErrors.onboarding_fee }"
                      />
                    </div>
                  </div>
                </div>
                <div class="col-lg-3 col-md-6">
                  <div class="form-group">
                    <label class="form-label">Currency <span class="text-danger">*</span></label>
                    <select
                      class="custom-select form-control-sm"
                      v-model="formData.currency"
                      :disabled="saving || currencyOptions.length === 0"
                    >
                      <option
                        v-for="opt in currencyOptions"
                        :key="opt.code"
                        :value="opt.code"
                      >
                        {{ opt.code }}
                      </option>
                    </select>
                    <small class="form-text text-muted">Loaded from business exchanges</small>
                  </div>
                </div>
              </div>
              <div class="row">
                  <div class="col-lg-3 col-md-6">
                    <div class="form-group">
                      <label class="form-label">Billing Contact <span class="text-danger">*</span></label>
                      <div class="input-group input-group-sm">
                        <textarea
                          class="form-control form-control-sm"
                          :class="{ 'is-invalid': validationErrors.billing_contact }"
                          v-model="formData.billing_contact"
                          :disabled="saving"
                          rows="3"
                          placeholder="Enter billing contact..."
                        ></textarea>
                      </div>
                      <div v-if="validationErrors.billing_contact" class="invalid-feedback d-block">
                        {{ validationErrors.billing_contact }}
                      </div>
                      <small class="form-text text-muted">Press Enter for new lines.</small>
                    </div>
                  </div>
                  <div class="col-lg-3 col-md-6">
                    <div class="form-group">
                      <label class="form-label">Onboarding Date</label>
                      <v-date-picker 
                        v-model="disbursedAtDate"
                        :disabled="saving"
                        :select-attribute="datePickerAttribute"
                        :input-debounce="300"
                      >
                        <template v-slot="{ inputValue, inputEvents }">
                          <div class="input-group input-group-sm">
                            <input
                              class="form-control form-control-sm"
                              :value="inputValue"
                              :disabled="saving"
                              placeholder="Select date"
                              readonly
                              v-on="inputEvents"
                            />
                            <div class="input-group-append">
                              <span class="input-group-text">
                                <i class="las la-calendar"></i>
                              </span>
                            </div>
                          </div>
                        </template>
                      </v-date-picker>
                      <small class="form-text text-muted">Date on which onboarding invoice was created.</small>
                    </div>
                  </div>
                  <div class="col-lg-3 col-md-6">
                    <div class="form-group">
                      <label class="form-label">Invoice Prefix</label>
                      <div class="input-group input-group-sm">
                        <input
                          type="text"
                          class="form-control form-control-sm"
                          :class="{ 'is-invalid': validationErrors.invoice_prefix }"
                          v-model="formData.invoice_prefix"
                          :disabled="saving"
                          placeholder="e.g., INV"
                          @input="sanitizeInvoicePrefix"
                        />
                      </div>
                      <div v-if="validationErrors.invoice_prefix" class="invalid-feedback d-block">
                        {{ validationErrors.invoice_prefix }}
                      </div>
                      <small class="form-text text-muted">Alphanumeric only, no special characters.</small>
                    </div>
                  </div>
                  <div class="col-lg-3 col-md-6">
                    <div class="form-group">
                      <label class="form-label">Financer IBAN</label>
                      <div class="input-group input-group-sm">
                        <input
                          type="text"
                          class="form-control form-control-sm"
                          :class="{ 'is-invalid': validationErrors.kema_iban }"
                          v-model="formData.kema_iban"
                          :disabled="saving"
                          placeholder="e.g., AE070331234567890123456"
                        />
                      </div>
                      <div v-if="validationErrors.kema_iban" class="invalid-feedback d-block">
                        {{ validationErrors.kema_iban }}
                      </div>
                      <small class="form-text text-muted">IBAN for the financer account.</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          <!-- Disbursement & Bank Details -->
          <div class="form-section">
            <h3 class="section-title">
              <i class="las la-university mr-2"></i>
              Disbursement & Bank Details
            </h3>
              <div class="row">
                <div class="col-lg-3 col-md-6">
                  <div class="form-group">
                    <label class="form-label">Minimum request amount <span class="text-danger">*</span></label>
                    <div class="input-group input-group-sm">
                      <input
                        type="number"
                        step="1"
                        class="form-control form-control-sm"
                        v-model.number="formData.minimum_disbursement_amount.limit"
                        placeholder="Amount"
                        :disabled="saving"
                        :class="{ 'is-invalid': validationErrors.minimum_disbursement_limit }"
                      />
                      <div class="input-group-append">
                        <select
                          class="custom-select form-control-sm"
                          v-model="formData.minimum_disbursement_amount.type"
                          :disabled="saving"
                          :class="{ 'is-invalid': validationErrors.minimum_disbursement_type }"
                        >
                          <option value="int">Fixed</option>
                          <option value="percentage">%</option>
                        </select>
                      </div>
                    </div>
                    <div v-if="validationErrors.minimum_disbursement_limit" class="invalid-feedback">{{ validationErrors.minimum_disbursement_limit }}</div>
                    <div v-if="validationErrors.minimum_disbursement_type" class="invalid-feedback">{{ validationErrors.minimum_disbursement_type }}</div>
                  </div>
                </div>
                <div class="col-lg-3 col-md-6">
                  <div class="form-group">
                    <label class="form-label">Account IBAN <span class="text-danger">*</span></label>
                    <div class="input-group input-group-sm">
                    <input
                      type="text"
                      class="form-control form-control-sm"
                      v-model="formData.account_iban"
                      placeholder="Enter IBAN"
                      :disabled="saving"
                      :class="{ 'is-invalid': validationErrors.account_iban }"
                    />
                    </div>
                    <div v-if="validationErrors.account_iban" class="invalid-feedback">{{ validationErrors.account_iban }}</div>
                  </div>
                </div>
                <div class="col-lg-3 col-md-6">
                  <div class="form-group">
                    <label class="form-label">Account Holder <span class="text-danger">*</span></label>
                    <div class="input-group input-group-sm">
                    <input
                      type="text"
                      class="form-control form-control-sm"
                      v-model="formData.account_holder_name"
                      placeholder="Enter name"
                      :disabled="saving"
                      :class="{ 'is-invalid': validationErrors.account_holder_name }"
                    />
                    </div>
                    <div v-if="validationErrors.account_holder_name" class="invalid-feedback">{{ validationErrors.account_holder_name }}</div>
                  </div>
                </div>
                <div class="col-lg-3 col-md-6">
                  <div class="form-group">
                    <label class="form-label">TRN (Optional)</label>
                    <div class="input-group input-group-sm">
                    <input
                      type="text"
                      class="form-control form-control-sm"
                      v-model="formData.trn"
                      placeholder="Tax Registration Number"
                      :disabled="saving"
                      :class="{ 'is-invalid': validationErrors.trn }"
                    />
                    </div>
                    <div v-if="validationErrors.trn" class="invalid-feedback">{{ validationErrors.trn }}</div>
                  </div>
                </div>
              </div>
            </div>

          <!-- First Row: Customer Groups & Financing Rules (50/50 split) -->
          <div class="main-sections-container">
            <!-- Left Section: Customer Groups Management -->
            <div class="eligible-payers-section">
              <div class="form-section">
                <div class="d-flex justify-content-between align-items-center mb-3">
                  <h3 class="section-title mb-0">
                    <i class="las la-users mr-2"></i>
                    Customer Groups
                  </h3>
                  <button
                    type="button"
                    class="btn btn-sm btn-success"
                    @click="openCreateGroupModal"
                    :disabled="saving || deletingGroup"
                  >
                    <i class="las la-plus mr-1"></i>
                    Create Group
                  </button>
                </div>

                <!-- Groups Loading State -->
                <div v-if="loadingGroups && availableGroups.length === 0" class="text-center py-3">
                  <div class="spinner-border spinner-border-sm mr-2"></div>
                  <small>Loading groups...</small>
                </div>

                <!-- Groups List -->
                <div 
                  v-else-if="availableGroups.length > 0" 
                  style="max-height: 350px; overflow-y: auto;"
                >
                  <div 
                    v-for="group in availableGroups" 
                    :key="`manage-group-${group.id}`"
                    class="customer-group-card border rounded mb-2 p-3"
                  >
                    <div class="d-flex justify-content-between align-items-center">
                      <div class="d-flex align-items-center flex-grow-1 min-w-0 mr-2">
                        <div class="d-flex align-items-center min-w-0 flex-grow-1">
                          <i class="las la-users text-info mr-2 flex-shrink-0" style="font-size: 1.2rem;"></i>
                          <div class="min-w-0 pr-2">
                            <strong class="d-block">{{ group.name }}</strong>
                            <small class="text-muted d-block">
                              {{ group.customerCount }} {{ group.customerCount === 1 ? 'customer' : 'customers' }}
                            </small>
                          </div>
                        </div>
                        <span
                          class="status-badge flex-shrink-0 ml-2"
                          :class="{ active: group.isActive, inactive: !group.isActive }"
                        >
                          {{ group.isActive ? 'Active' : 'Inactive' }}
                        </span>
                      </div>
                      <div class="d-flex align-items-center flex-shrink-0">
                        <button
                          type="button"
                          class="btn btn-sm btn-outline-primary mr-1"
                          style="padding: 0.25rem 0.5rem; font-size: 0.75rem;"
                          @click="openEditGroupModal(group)"
                          :disabled="saving || deletingGroup"
                          title="Edit group"
                        >
                          <i class="las la-edit"></i>
                        </button>
                        <button
                          type="button"
                          class="btn btn-sm btn-outline-danger"
                          style="padding: 0.25rem 0.5rem; font-size: 0.75rem;"
                          @click="openDeleteGroupModal(group)"
                          :disabled="saving || deletingGroup"
                          title="Delete group"
                        >
                          <i class="las la-trash"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- No Groups State -->
                <div v-else class="text-center py-5 text-muted">
                  <i class="las la-users-slash" style="font-size: 2rem;"></i>
                  <p class="mb-0 mt-2">No customer groups</p>
                  <small>Create a group to get started</small>
                </div>
              </div>
            </div>

            <!-- Right Section: Financing Rules -->
            <div class="financing-rules-section">
              <div class="form-section">
                <h3 class="section-title">
                  <i class="las la-cogs mr-2"></i>
                  Financing Rules
                </h3>
                    
                <!-- Rules Loading State -->
                <div v-if="loadingRules" class="text-center py-3">
                  <div class="spinner-border spinner-border-sm mr-2"></div>
                  <small>Loading rules...</small>
                </div>
                
                <!-- Rules List -->
                <div v-else-if="availableRules.length">
                  <div class="rules-list-container">
                    <div 
                      v-for="rule in availableRules" 
                      :key="rule.id"
                      class="border rounded mb-2 p-2 rule-item"
                      :class="{ 'assigned': rule.assigned }"
                    >
                      <div class="d-flex justify-content-between align-items-center mb-2">
                        <div class="custom-control custom-checkbox">
                          <input
                            type="checkbox"
                            class="custom-control-input"
                            :id="`rule-${rule.id}`"
                            v-model="rule.assigned"
                            :disabled="saving"
                            @change="onRuleAssignmentChange(rule)"
                          />
                          <label class="custom-control-label" :for="`rule-${rule.id}`">
                            <strong style="font-size: 0.8rem;">{{ rule.name }}</strong>
                          </label>
                        </div>
                      </div>
                      
                      <!-- Threshold Input -->
                      <div v-if="rule.assigned" class="mt-2 threshold-input">
                        <label class="form-label" style="font-size: 0.75rem;">Threshold:</label>
                        <div class="input-group input-group-sm">
                          <input
                            type="number"
                            step="0.1"
                            min="0"
                            max="1"
                            class="form-control form-control-sm"
                            v-model.number="rule.threshold"
                            :disabled="saving"
                            style="font-size: 0.75rem;"
                            @input="onRuleThresholdChange(rule)"
                          />
                          <div class="input-group-append">
                            <span class="input-group-text" style="font-size: 0.75rem;">max 1.0</span>
                          </div>
                        </div>
                        <small class="form-text text-muted">Value between 0 and 1</small>
                      </div>
                    </div>
                  </div>
                  
                  <!-- Rules Summary -->
                  <div class="mt-3 p-2 rounded rules-summary">
                    <small class="text-muted">
                      <i class="las la-info-circle mr-1"></i>
                      {{ assignedRulesCount }} of {{ availableRules.length }} rules assigned
                      <span v-if="rulesModified" class="text-warning ml-2">
                        <i class="las la-exclamation-triangle"></i>
                        Unsaved changes
                      </span>
                    </small>
                  </div>
                </div>
                
                <!-- No Rules State -->
                <div v-else class="text-center py-3 text-muted">
                  <i class="las la-info-circle" style="font-size: 2rem;"></i>
                  <p class="mb-0 mt-2">No financing rules available</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Eligible Payers Configuration (Full Width Below) -->
          <div class="eligible-payers-full-width">
            <div class="form-section">
              <!-- Section Header with Tabs -->
              <div class="d-flex justify-content-between align-items-center mb-3">
                <h3 class="section-title mb-0">
                  <i class="las la-building mr-2"></i>
                  Eligible Payers Configuration
                </h3>
                
                <!-- Tabs for Customer vs Group -->
                <div class="btn-group btn-group-sm" role="group">
                  <button 
                    type="button" 
                    class="btn btn-sm"
                    :class="activePayerTab === 'customers' ? 'btn-primary' : 'btn-outline-primary'"
                    @click="activePayerTab = 'customers'"
                    :disabled="saving"
                  >
                    <i class="las la-user mr-1"></i>
                    Individual Customers
                  </button>
                  <button 
                    type="button" 
                    class="btn btn-sm"
                    :class="activePayerTab === 'groups' ? 'btn-primary' : 'btn-outline-primary'"
                    @click="activePayerTab = 'groups'"
                    :disabled="saving"
                  >
                    <i class="las la-users mr-1"></i>
                    Customer Groups
                  </button>
                </div>
              </div>

              <!-- Search Boxes (conditional based on active tab) -->
              <div class="mb-3">
                <!-- Customer Search -->
                <div v-if="activePayerTab === 'customers'" class="business-search-container">
                  <div class="d-flex align-items-center">
                    <div class="position-relative flex-grow-1">
                      <input
                        ref="searchInput"
                        type="text"
                        class="form-control form-control-sm"
                        v-model="businessSearchTerm"
                        @input="searchBusinesses"
                        @focus="showBusinessDropdown = true"
                        @blur="handleSearchBlur"
                        placeholder="Search and add customers..."
                        :disabled="saving"
                      />
                      <i class="las la-search position-absolute" style="right: 10px; top: 50%; transform: translateY(-50%);"></i>
                    </div>
                  </div>
                  
                  <div 
                    v-if="showBusinessDropdown" 
                    ref="businessDropdown"
                    class="dropdown-menu show position-absolute w-100" 
                    style="z-index: 1000; max-height: 300px; overflow-y: auto;"
                    @mouseenter="isInteractingWithDropdown = true"
                    @mouseleave="isInteractingWithDropdown = false"
                    @mousedown.prevent
                    @scroll="handleDropdownScroll"
                  >
                    <div v-if="loadingBusinesses && !filteredBusinesses.length" class="dropdown-item-text">
                      <div class="spinner-border spinner-border-sm mr-2"></div>
                      Loading customers...
                    </div>
                    
                    <div v-else-if="!filteredBusinesses.length" class="dropdown-item-text">
                      No customers found
                    </div>
                    
                    <template v-else>
                      <div 
                        v-for="business in filteredBusinesses" 
                        :key="business.id"
                        class="dropdown-item"
                        :class="{ 
                          'active': isPayerSelected(business),
                          'disabled': isCustomerInSelectedGroup(business.id)
                        }"
                        @mousedown="handlePayerClick(business, $event)"
                        :style="isCustomerInSelectedGroup(business.id) ? 'cursor: not-allowed; opacity: 0.6;' : 'cursor: pointer;'"
                      >
                        <div class="d-flex justify-content-between align-items-center">
                          <div class="flex-grow-1">
                            <span style="font-size: 0.8rem;">{{ business.name }}</span>
                            <small v-if="isCustomerInSelectedGroup(business.id)" class="d-block text-warning">
                              <i class="las la-exclamation-triangle"></i>
                              In group "{{ isCustomerInSelectedGroup(business.id) }}"
                            </small>
                          </div>
                          <i 
                            v-if="!isCustomerInSelectedGroup(business.id)"
                            :class="isPayerSelected(business) ? 'las la-check text-success' : 'las la-plus text-primary'" 
                            style="font-size: 0.8rem;"
                          ></i>
                        </div>
                      </div>
                      
                      <div v-if="isLoadingMore" class="dropdown-item-text">
                        <div class="spinner-border spinner-border-sm mr-2"></div>
                        Loading more...
                      </div>
                    </template>
                  </div>
                </div>

                <!-- Group Search -->
                <div v-if="activePayerTab === 'groups'" class="business-search-container">
                  <div class="d-flex align-items-center">
                    <div class="position-relative flex-grow-1">
                      <input
                        ref="groupSearchInput"
                        type="text"
                        class="form-control form-control-sm"
                        v-model="groupSearchTerm"
                        @input="searchGroups"
                        @focus="showGroupDropdown = true"
                        @blur="handleGroupSearchBlur"
                        placeholder="Search and add customer groups..."
                        :disabled="saving"
                      />
                      <i class="las la-search position-absolute" style="right: 10px; top: 50%; transform: translateY(-50%);"></i>
                    </div>
                  </div>
                  
                  <div 
                    v-if="showGroupDropdown" 
                    ref="groupDropdown"
                    class="dropdown-menu show position-absolute" 
                    style="z-index: 1000; max-height: 300px; overflow-y: auto; width: 100%;"
                    @mouseenter="isInteractingWithGroupDropdown = true"
                    @mouseleave="isInteractingWithGroupDropdown = false"
                    @mousedown.prevent
                    @scroll="handleGroupDropdownScroll"
                  >
                    <div v-if="loadingGroups && !filteredGroups.length" class="dropdown-item-text">
                      <div class="spinner-border spinner-border-sm mr-2"></div>
                      Loading groups...
                    </div>
                    
                    <div v-else-if="!filteredGroups.length" class="dropdown-item-text">
                      No customer groups found
                    </div>
                    
                    <template v-else>
                      <div 
                        v-for="group in filteredGroups" 
                        :key="group.id"
                        class="dropdown-item"
                        :class="{ 
                          'active': isPayerSelected(group),
                          'disabled': getGroupConflictingCustomers(group.id).length > 0
                        }"
                        @mousedown="handlePayerClick(group, $event)"
                        :style="getGroupConflictingCustomers(group.id).length > 0 ? 'cursor: not-allowed; opacity: 0.6;' : 'cursor: pointer;'"
                      >
                        <div class="d-flex justify-content-between align-items-center">
                          <div class="flex-grow-1">
                            <strong style="font-size: 0.8rem;">{{ group.name }}</strong>
                            <small class="d-block text-muted">{{ group.customerCount }} {{ group.customerCount === 1 ? 'customer' : 'customers' }}</small>
                            <small 
                              v-if="getGroupConflictingCustomers(group.id).length > 0" 
                              class="d-block text-warning"
                            >
                              <i class="las la-exclamation-triangle"></i>
                              {{ getGroupConflictingCustomers(group.id).length }} customer(s) already added
                            </small>
                          </div>
                          <i 
                            v-if="getGroupConflictingCustomers(group.id).length === 0"
                            :class="isPayerSelected(group) ? 'las la-check text-success' : 'las la-plus text-primary'" 
                            style="font-size: 0.8rem;"
                          ></i>
                        </div>
                      </div>
                      
                      <div v-if="isLoadingMoreGroups" class="dropdown-item-text">
                        <div class="spinner-border spinner-border-sm mr-2"></div>
                        Loading more...
                      </div>
                    </template>
                  </div>
                </div>
              </div>

              <!-- Payer Configurations Table -->
              <div v-if="selectedPayers.length" class="table-responsive mt-3">
                  <table class="table table-sm">
                    <thead>
                      <tr>
                        <th width="5%">Type</th>
                        <th width="20%">Name</th>
                        <th width="35%">Transaction Fees (Days → %)</th>
                        <th width="15%">Max Limit %</th>
                        <th width="15%">Advance Rate %</th>
                        <th width="10%">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="payer in selectedPayers" :key="`${payer.type}:${payer.id}`">
                        <td>
                          <span 
                            class="badge badge-sm"
                            :class="payer.type === 'customer' ? 'badge-primary' : 'badge-info'"
                          >
                            <i :class="payer.type === 'customer' ? 'las la-user' : 'las la-users'"></i>
                          </span>
                        </td>
                        <td>
                          <strong style="font-size: 0.75rem;">{{ payer.name }}</strong>
                          <small v-if="payer.type === 'group'" class="d-block text-muted">Group</small>
                        </td>
                        <td>
                          <div class="d-flex flex-column">
                            <div 
                              v-for="(fee, feeIndex) in getPayerTransactionFees(payer)" 
                              :key="feeIndex"
                              class="d-flex align-items-center mb-2"
                            >
                              <input
                                type="number"
                                min="1"
                                class="form-control form-control-sm mr-2 no-spinner"
                                style="width: 25%; text-align: center;"
                                v-model.number="fee.day"
                                placeholder="Days"
                                :disabled="saving"
                                @input="updateFeeField(payer, feeIndex, 'day', $event.target.value)"
                              />
                              <span class="mr-2">→</span>
                              <input
                                type="number"
                                step="0.5"
                                class="form-control form-control-sm mr-2 no-spinner"
                                style="width: 25%; text-align: center;"
                                v-model.number="fee.percentage"
                                placeholder="%"
                                :disabled="saving"
                                @input="updateFeeField(payer, feeIndex, 'percentage', $event.target.value)"
                              />
                              <button
                                type="button"
                                class="btn btn-sm btn-outline-danger btn-icon"
                                @click="removePayerTransactionFee(payer, feeIndex)"
                                :disabled="saving || getPayerTransactionFees(payer).length === 1"
                                style="font-size: 0.7rem;"
                              >
                                <i class="las la-minus"></i>
                              </button>
                            </div>
                            <button
                              type="button"
                              class="btn btn-sm btn-outline-primary align-self-start"
                              @click="addPayerTransactionFee(payer)"
                              :disabled="saving"
                              style="font-size: 0.7rem;"
                            >
                              Add
                            </button>
                          </div>
                        </td>
                        <td>
                          <div class="input-group input-group-sm">
                            <input
                              type="number"
                              step="1"
                              min="0"
                              max="100"
                              class="form-control form-control-sm no-spinner"
                              v-model.number="getPayerConfig(payer).limit_allowed"
                              placeholder="%"
                              :disabled="saving"
                              style="text-align: center;"
                            />
                            <div class="input-group-append">
                              <span class="input-group-text">%</span>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div class="input-group input-group-sm">
                            <input
                              type="number"
                              step="1"
                              min="0"
                              max="100"
                              class="form-control form-control-sm no-spinner"
                              v-model.number="getPayerConfig(payer).advance_rate"
                              placeholder="%"
                              :disabled="saving"
                              style="text-align: center;"
                            />
                            <div class="input-group-append">
                              <span class="input-group-text">%</span>
                            </div>
                          </div>
                        </td>
                        <td>
                          <button 
                            type="button" 
                            class="btn btn-sm btn-outline-danger btn-icon"
                            @click="removePayerFromSelected(payer)"
                            :disabled="saving"
                            style="font-size: 0.7rem;"
                          >
                            <i class="las la-times"></i>
                          </button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
            </div>
          </div>
        
        <!-- Footer Actions -->
        <div class="d-flex justify-content-between align-items-center mt-4 pt-3 border-top">
          <button class="btn btn-secondary" :disabled="saving" @click="goBack">
            <i class="las la-arrow-left mr-2"></i>
            Cancel
          </button>
          
          <div class="d-flex align-items-center">
            <!-- Credit Line Active Toggle -->
            <div class="custom-control custom-switch mr-4">
              <input
                type="checkbox"
                class="custom-control-input"
                id="creditLineActiveToggle"
                v-model="formData.is_active"
                :disabled="saving"
              />
              <label class="custom-control-label" for="creditLineActiveToggle">
                <strong>Credit Line Active</strong>
              </label>
            </div>
            
            <!-- Financing Emails Toggle -->
            <div class="custom-control custom-switch mr-4">
              <input
                type="checkbox"
                class="custom-control-input"
                id="financingEmailsToggle"
                v-model="formData.financing_emails_enabled"
                :disabled="saving"
              />
              <label class="custom-control-label" for="financingEmailsToggle">
                <strong>Financing Emails Enabled</strong>
              </label>
            </div>
            
            <button class="btn btn-primary" :disabled="saving || loading" @click="submit">
              <span v-if="saving">
                <div class="spinner-border spinner-border-sm mr-2"></div>
                Saving...
              </span>
              <span v-else>
                <i class="las la-save mr-2"></i>
                {{ hasExistingData ? 'Update Settings' : 'Create Credit Line' }}
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Create / Edit customer group modal -->
    <div
      v-if="showGroupModal"
      class="modal fade show d-block"
      tabindex="-1"
      role="dialog"
      style="background-color: rgba(0,0,0,0.5); overflow-y: auto;"
    >
      <div class="modal-dialog modal-dialog-centered modal-lg" role="document">
        <div class="modal-content">
          <div class="modal-header border-bottom-0 pb-0 justify-content-center position-relative">
            <h5 class="modal-title text-center px-4">
              {{ groupModalMode === 'edit' ? 'Edit Group' : 'Create Group' }}
            </h5>
            <button
              type="button"
              class="close position-absolute"
              style="right: 1rem; top: 0.75rem;"
              @click="closeGroupModal"
              :disabled="savingGroup || groupModalInitialLoading"
            >
              <span>&times;</span>
            </button>
          </div>
          <div class="modal-body position-relative">
            <div v-if="groupValidationErrors.general" class="alert alert-danger">
              {{ groupValidationErrors.general }}
            </div>

            <div
              v-if="groupModalInitialLoading"
              class="text-center py-5"
            >
              <div class="spinner-border text-primary mb-2"></div>
              <p class="text-muted mb-0">Loading…</p>
            </div>

            <template v-else>
              <div class="form-group">
                <input
                  type="text"
                  class="form-control"
                  v-model="groupForm.name"
                  placeholder="Group Name*"
                  :disabled="savingGroup"
                  :class="{ 'is-invalid': groupValidationErrors.name }"
                />
                <div v-if="groupValidationErrors.name" class="invalid-feedback d-block">
                  {{ groupValidationErrors.name }}
                </div>
              </div>

              <div class="form-group">
                <input
                  type="text"
                  class="form-control"
                  v-model="groupForm.contactName"
                  placeholder="Contact Name"
                  :disabled="savingGroup"
                />
              </div>

              <div class="form-group">
                <label class="form-label">Customers</label>
                <span class="text-muted small ml-2">({{ groupForm.selectedCustomers.length }} selected)</span>

                <div
                  v-if="getGroupModalHiddenCustomersCount() > 0"
                  class="alert alert-info py-2 mb-2"
                >
                  <i class="las la-info-circle mr-1"></i>
                  <small>
                    {{ getGroupModalHiddenCustomersCount() }} customer(s) are hidden (already added individually or in other groups)
                  </small>
                </div>

                <div class="d-flex mb-2">
                  <input
                    type="text"
                    class="form-control form-control-sm mr-2"
                    v-model="groupModalCustomerSearchTerm"
                    @input="searchGroupModalCustomers"
                    placeholder="Search or select customers..."
                    :disabled="savingGroup || loadingGroupModalCustomers"
                  />
                  <button
                    type="button"
                    class="btn btn-sm btn-outline-secondary"
                    @click="clearGroupFormCustomers"
                    :disabled="savingGroup || loadingGroupModalCustomers || groupForm.selectedCustomers.length === 0"
                    title="Clear selection"
                  >
                    <i class="las la-times"></i>
                  </button>
                </div>

                <div
                  v-if="loadingGroupModalCustomers && groupModalCustomerOptions.length === 0"
                  class="text-center py-3 border rounded"
                >
                  <div class="spinner-border spinner-border-sm mr-2"></div>
                  Loading customers...
                </div>

                <div
                  v-else-if="groupModalCustomerOptions.length > 0"
                  ref="groupModalCustomerScrollContainer"
                  class="border rounded p-2"
                  style="max-height: 350px; overflow-y: scroll; overflow-x: hidden; -webkit-overflow-scrolling: touch;"
                  @scroll.stop="handleGroupModalCustomerScroll"
                  @wheel.stop
                >
                  <div
                    v-for="customer in groupModalCustomerOptions"
                    :key="`gm-cust-${customer.id}`"
                    class="custom-control custom-checkbox mb-2"
                    :class="{ 'text-muted': isGroupModalCustomerInConflict(customer.id) }"
                  >
                    <input
                      type="checkbox"
                      class="custom-control-input"
                      :id="`group-modal-cust-${customer.id}`"
                      :checked="isGroupModalCustomerSelected(customer.id)"
                      @change="toggleGroupModalCustomer(customer.id)"
                      :disabled="savingGroup || isGroupModalCustomerInConflict(customer.id)"
                    />
                    <label
                      class="custom-control-label"
                      :for="`group-modal-cust-${customer.id}`"
                      style="font-size: 0.875rem;"
                    >
                      {{ customer.name }}
                      <small v-if="isGroupModalCustomerInConflict(customer.id)" class="text-warning ml-2">
                        (Already in use)
                      </small>
                    </label>
                  </div>

                  <div v-if="isLoadingMoreGroupModalCustomers" class="text-center py-2">
                    <div class="spinner-border spinner-border-sm mr-2"></div>
                    <small>Loading more...</small>
                  </div>
                </div>

                <div v-else class="text-center text-muted py-3 border rounded">
                  <i class="las la-info-circle" style="font-size: 1.5rem;"></i>
                  <p class="mb-0">No customers available</p>
                </div>
              </div>

              <div class="form-group">
                <label class="form-label">To emails</label>
                <div
                  class="border rounded p-2 d-flex flex-wrap align-items-center"
                  style="gap: 0.35rem; min-height: 2.5rem;"
                >
                  <span
                    v-for="(em, i) in groupForm.toEmails"
                    :key="'to-em-' + i"
                    class="badge badge-secondary d-inline-flex align-items-center"
                  >
                    {{ em }}
                    <button
                      type="button"
                      class="btn btn-link btn-sm text-white p-0 ml-2"
                      style="font-size: 1rem; line-height: 1;"
                      @click="removeEmailChip('to', i)"
                      :disabled="savingGroup"
                    >&times;</button>
                  </span>
                  <input
                    type="text"
                    class="form-control form-control-sm border-0 shadow-none flex-grow-1"
                    style="min-width: 120px;"
                    v-model="groupModalEmailDraftTo"
                    placeholder="Add email…"
                    :disabled="savingGroup"
                    @keydown.enter.prevent="commitEmailDraft('to')"
                    @keydown="onEmailDraftKeydown($event, 'to')"
                    @blur="commitEmailDraft('to')"
                  />
                </div>
              </div>

              <div class="form-group">
                <label class="form-label">CC emails</label>
                <div
                  class="border rounded p-2 d-flex flex-wrap align-items-center"
                  style="gap: 0.35rem; min-height: 2.5rem;"
                >
                  <span
                    v-for="(em, i) in groupForm.ccEmails"
                    :key="'cc-em-' + i"
                    class="badge badge-secondary d-inline-flex align-items-center"
                  >
                    {{ em }}
                    <button
                      type="button"
                      class="btn btn-link btn-sm text-white p-0 ml-2"
                      style="font-size: 1rem; line-height: 1;"
                      @click="removeEmailChip('cc', i)"
                      :disabled="savingGroup"
                    >&times;</button>
                  </span>
                  <input
                    type="text"
                    class="form-control form-control-sm border-0 shadow-none flex-grow-1"
                    style="min-width: 120px;"
                    v-model="groupModalEmailDraftCc"
                    placeholder="Add email…"
                    :disabled="savingGroup"
                    @keydown.enter.prevent="commitEmailDraft('cc')"
                    @keydown="onEmailDraftKeydown($event, 'cc')"
                    @blur="commitEmailDraft('cc')"
                  />
                </div>
              </div>

              <div class="form-group" v-if="canAssignCustomerGroupWorkflow">
                <label class="form-label">Reminder workflow</label>
                <select
                  class="custom-select form-control"
                  v-model="groupForm.workflowId"
                  :disabled="savingGroup"
                >
                  <option value="">—</option>
                  <option
                    v-for="w in groupModalWorkflowOptions"
                    :key="'wf-' + w.id"
                    :value="w.id"
                  >
                    {{ w.name }}
                  </option>
                </select>
              </div>

              <div class="form-group d-flex align-items-center justify-content-between">
                <label class="form-label mb-0" for="combineStatementsToggle">
                  Combine customer statements
                </label>
                <div class="custom-control custom-switch">
                  <input
                    type="checkbox"
                    class="custom-control-input"
                    id="combineStatementsToggle"
                    v-model="groupForm.combineStatements"
                    :disabled="savingGroup"
                  />
                  <label class="custom-control-label" for="combineStatementsToggle"></label>
                </div>
              </div>

              <div
                class="border-top pt-3 mt-2 d-flex align-items-center justify-content-between flex-wrap"
                style="gap: 0.5rem 1rem;"
              >
                <div class="d-flex align-items-center flex-wrap">
                  <span class="text-muted small mb-0 mr-2">Status:</span>
                  <span
                    class="status-badge"
                    :class="{ active: groupForm.isActive, inactive: !groupForm.isActive }"
                  >
                    {{ groupForm.isActive ? 'Active' : 'Inactive' }}
                  </span>
                </div>
                <button
                  v-if="groupModalMode === 'edit'"
                  type="button"
                  class="btn btn-outline-primary btn-sm text-nowrap"
                  @click="toggleGroupActiveFromModal"
                  :disabled="savingGroup || togglingGroupActive || groupModalInitialLoading"
                >
                  <span v-if="togglingGroupActive" class="spinner-border spinner-border-sm mr-1" role="status"></span>
                  {{ groupForm.isActive ? 'Set Inactive' : 'Set Active' }}
                </button>
              </div>
            </template>
          </div>
          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-link"
              @click="closeGroupModal"
              :disabled="savingGroup || groupModalInitialLoading"
            >
              Cancel
            </button>
            <button
              type="button"
              class="btn btn-primary text-uppercase"
              @click="submitGroupModal"
              :disabled="savingGroup || groupModalInitialLoading"
            >
              <span v-if="savingGroup">
                <div class="spinner-border spinner-border-sm mr-2"></div>
                {{ groupModalMode === 'edit' ? 'Saving…' : 'Creating…' }}
              </span>
              <span v-else>
                {{ groupModalMode === 'edit' ? 'Save' : 'Create' }}
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete customer group confirmation -->
    <div
      v-if="showDeleteGroupModal"
      class="modal fade show d-block"
      tabindex="-1"
      role="dialog"
      aria-labelledby="deleteGroupModalTitle"
      style="background-color: rgba(0,0,0,0.5); overflow-y: auto;"
      @click.self="closeDeleteGroupModal"
    >
      <div
        class="modal-dialog modal-dialog-centered"
        role="document"
        style="max-width: 420px;"
        @click.stop
      >
        <div class="modal-content rounded shadow">
          <div class="modal-header border-0 pb-0 pt-4 px-4">
            <h5 id="deleteGroupModalTitle" class="modal-title text-dark mb-0">
              Delete Group
            </h5>
          </div>
          <div class="modal-body px-4 pt-3 pb-2">
            <p v-if="!deleteGroupModalBlockedReason" class="mb-0 text-secondary">
              Are you sure you want to delete the group &quot;{{ groupPendingDelete ? groupPendingDelete.name : '' }}&quot;?
            </p>
            <div v-else class="alert alert-warning mb-0 py-2" role="alert">
              {{ deleteGroupModalBlockedReason }}
            </div>
          </div>
          <div class="modal-footer border-0 pt-2 pb-4 px-4 justify-content-end">
            <button
              type="button"
              class="btn btn-link px-3"
              @click="closeDeleteGroupModal"
              :disabled="deletingGroup"
            >
              Cancel
            </button>
            <button
              type="button"
              class="btn btn-primary px-4 text-uppercase"
              @click="confirmDeleteGroup"
              :disabled="!!deleteGroupModalBlockedReason || deletingGroup || !groupPendingDelete"
            >
              <span v-if="deletingGroup">
                <span class="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                Deleting…
              </span>
              <span v-else>Confirm</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import FinancingService from "@/services/financing.service";
import CompaniesService from "@/services/companies.service";
import RulesService from "@/services/rules.service";
import { PERMISSIONS } from "@/constants";
import { UserService } from "@/storage";
import { formatErrorMessage } from "@/utils/random_utils";
import { DateTime } from "luxon";

export default {
  name: "FinancingSettingsPage",
  data() {
    return {
      businessId: null,
      businessName: '',
      businessExchanges: [],
      creditLineId: null,
      loading: false,
      saving: false,
      error: null,
      successMessage: null,
      hasExistingData: false,
      validationErrors: {},

      // Business selection - Customers and Groups
      availableBusinesses: [],
      availableGroups: [],
      selectedPayers: [], // Combined list with type indicator
      businessSearchTerm: "",
      groupSearchTerm: "",
      showBusinessDropdown: false,
      showGroupDropdown: false,
      loadingBusinesses: false,
      loadingGroups: false,
      businessSearchDebounce: null,
      groupSearchDebounce: null,
      businessPage: 1,
      groupPage: 1,
      hasMoreBusinesses: false,
      hasMoreGroups: false,
      isInteractingWithDropdown: false,
      isInteractingWithGroupDropdown: false,
      isLoadingMore: false,
      isLoadingMoreGroups: false,
      activePayerTab: 'customers', // 'customers' or 'groups'

      // Financing Rules
      availableRules: [],
      loadingRules: false,
      originalRulesData: [],
      rulesModified: false,

      // Create / Edit customer group modal (single modal)
      showGroupModal: false,
      groupModalMode: "create",
      groupModalEditingId: null,
      groupModalInitialLoading: false,
      savingGroup: false,
      groupForm: {
        name: "",
        contactName: "",
        selectedCustomers: [],
        toEmails: [],
        ccEmails: [],
        workflowId: "",
        combineStatements: false,
        isActive: true,
      },
      groupModalWorkflowOptions: [],
      groupModalMemberExtras: [],
      groupModalEmailDraftTo: "",
      groupModalEmailDraftCc: "",
      groupValidationErrors: {},
      groupModalCustomerSearchTerm: "",
      loadingGroupModalCustomers: false,
      groupModalFetchedCustomers: [],
      groupModalCustomerPage: 1,
      hasMoreGroupModalCustomers: false,
      isLoadingMoreGroupModalCustomers: false,
      groupModalCustomerDebounce: null,
      togglingGroupActive: false,

      // Delete group confirmation modal
      showDeleteGroupModal: false,
      groupPendingDelete: null,
      deleteGroupModalBlockedReason: null,
      deletingGroup: false,

      // Group members tracking
      groupMembers: {}, // Format: { 'group-id': ['customer-id-1', 'customer-id-2'] }

      formData: {
        limit: null,
        settlement_fee: null,
        onboarding_fee: null,
        currency: "AED",
        eligible_businesses: {},
        account_iban: "",
        account_holder_name: "",
        trn: "",
        onboarding_invoice_send: true,
        financing_emails_enabled: true,
        is_active: true,
        minimum_disbursement_amount: {
          limit: null,
          type: "int"
        },
        billing_contact: "",
        invoice_prefix: "",
        kema_iban: ""
      },
      disbursedAtDate: new Date(),
      payerConfigs: {}, // Format: { 'customer:uuid': {...}, 'group:uuid': {...} }
      originalData: null,
      alertTimeouts: {}
    };
  },
  computed: {
    filteredBusinesses() {
      return this.availableBusinesses.filter(b => b.id !== this.businessId);
    },
    filteredGroups() {
      return this.availableGroups;
    },
    assignedRulesCount() {
      return this.availableRules.filter(rule => rule.assigned).length;
    },
    currencyOptions() {
      if (Array.isArray(this.businessExchanges) && this.businessExchanges.length) {
        return this.businessExchanges.map(ex => {
          const code = ex.currency || ex.code || ex.id;
          const base = ex.base_currency || this.formData.currency;
          const rate = ex.rate;
          const rateText = (rate && base && code)
            ? `1 ${code} = ${rate} ${base}`
            : '';
          return { code, rateText };
        });
      }
      return this.formData.currency ? [{ code: this.formData.currency, rateText: '' }] : [];
    },
    datePickerAttribute() {
      return {
        key: 'today',
        highlight: {
          color: 'blue',
          fillMode: 'light'
        },
        dates: new Date()
      };
    },
    canAssignCustomerGroupWorkflow() {
      const u = UserService.getUser() || {};
      const perms = u.permissions || [];
      return !!(u.is_staff || u.is_superuser || perms.includes(PERMISSIONS.FINANCING_ALL));
    },
    groupModalCustomerOptions() {
      return this.mergeGroupModalCustomerLists(
        this.groupModalFetchedCustomers,
        this.groupModalMemberExtras
      );
    },
  },
  async mounted() {
    this.businessId = this.$route.params.business_id;
    if (!this.businessId) {
      this.$router.push('/financing/businesses');
      return;
    }

    document.addEventListener('click', this.handleClickOutside);
    document.addEventListener('keydown', this.handleKeydown);
    
    // Setup auto-hide for alerts
    this.setupAutoHideAlerts();
    
    await this.initializeSettings();
  },
  
  watch: {
    error(newVal) {
      if (newVal) {
        this.autoHideAlert('error');
      }
    },
    successMessage(newVal) {
      if (newVal) {
        this.autoHideAlert('success');
      }
    },
    showGroupModal() {
      this.syncModalBodyOverflow();
    },
    showDeleteGroupModal() {
      this.syncModalBodyOverflow();
    },
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside);
    document.removeEventListener('keydown', this.handleKeydown);
    clearTimeout(this.businessSearchDebounce);
    clearTimeout(this.groupSearchDebounce);
    clearTimeout(this.groupModalCustomerDebounce);
    // Restore body overflow in case modal was open
    document.body.style.overflow = '';
  },
  methods: {
    async initializeSettings() {
      this.loading = true;
      try {
        // Load business data and financing data first to determine if credit line exists
        await Promise.all([
          this.loadBusinessData(),
          this.loadFinancingData(),
          this.loadInitialBusinesses(),
          this.loadInitialGroups()
        ]);
        
        // Then load financing rules based on whether credit line exists
        await this.loadFinancingRules();
      } catch (err) {
        console.error('Failed to initialize:', err);
        this.error = 'Failed to load settings';
      } finally {
        this.loading = false;
      }
    },

    async loadBusinessData() {
      try {
        const response = await CompaniesService.get(this.businessId, {list_view: true});
        if (response?.data) {
          this.formData.currency = response.data.base_currency || 'AED';
          this.businessName = response.data.name || 'Unknown Business';
          this.businessExchanges = response.data.exchanges || [];
          // Load financing_enabled status from business data
          this.formData.is_active = response.data.financing_enabled !== false;
        }
      } catch (err) {
        console.error('Failed to load business data:', err);
        this.formData.currency = 'AED';
        this.businessExchanges = [];
        this.formData.is_active = true; // Default to true on error
      }
    },

    async loadFinancingData() {
      try {
        const response = await FinancingService.getFinancingForBusiness(this.businessId);
        if (response?.data?.id && response.data.customers) {
          await this.parseExistingData(response.data);
        } else {
          this.resetToNewData();
        }
      } catch (err) {
        this.error = "Failed to load financing data.";
        this.resetToNewData();
      }
    },

    async parseExistingData(data) {
      this.hasExistingData = true;
      this.creditLineId = data.id;

      // Load selected payers (both customers and groups)
      this.selectedPayers = (data.customers || [])
        .filter(c => c.customer_id || c.customer_group__id)
        .map(c => {
          const isGroup = !!c.customer_group__id;
          return {
            id: isGroup ? String(c.customer_group__id) : String(c.customer_id),
            name: isGroup ? (c.customer_group_name || `Group ${c.customer_group__id}`) : (c.customer_name || `Business ${c.customer_id}`),
            type: isGroup ? 'group' : 'customer',
            junctionId: c.id // Store the relationship ID for updates/deletes
          };
        });

      // Fetch group members for all selected groups from the groups API
      const selectedGroupIds = this.selectedPayers
        .filter(p => p.type === 'group')
        .map(g => g.id);
      
      if (selectedGroupIds.length > 0) {
        try {
          // Fetch all groups to get their members
          const response = await FinancingService.getCustomerGroups({
            business_id: this.businessId,
            page_size: 100 // Get enough to include all selected groups
          });
          
          // Cache members for selected groups
          (response.data || [])
            .filter(group => selectedGroupIds.includes(group.id))
            .forEach((group) => {
              const customers = Array.isArray(group.customers)
                ? group.customers
                : [];
              if (customers.length > 0) {
                this.$set(this.groupMembers, group.id, customers);
              }
            });
        } catch (err) {
          console.error('Failed to fetch group members for existing data:', err);
        }
      }

      // Parse configurations with type prefix
      const eligibleBusinesses = {};
      const payerConfigs = {};
      
      if (data.customers?.length) {
        data.customers.forEach(customer => {
          const isGroup = !!customer.customer_group__id;
          const payerId = isGroup ? customer.customer_group__id : customer.customer_id;
          const payerKey = `${isGroup ? 'group' : 'customer'}:${payerId}`;
          
          if (payerId) {
            eligibleBusinesses[payerId] = {
              ...customer,
              type: isGroup ? 'group' : 'customer',
              customer_id: customer.customer_id,
              customer_group_id: customer.customer_group__id
            };
            
            const tf = customer.additional_details?.transaction_fees || {};
            const tfObj = typeof tf === 'object' && tf !== null && !Array.isArray(tf) ? tf : {};
            const order = Object.keys(tfObj).filter(d => d && d !== 'null').map(Number);
            payerConfigs[payerKey] = {
              type: isGroup ? 'group' : 'customer',
              id: payerId,
              junctionId: customer.id,
              transaction_fees: { ...tfObj },
              transaction_fees_order: order,
              limit_allowed: customer.additional_details?.limit_allowed || 0,
              advance_rate: customer.additional_details?.advance_rate || 100
            };
          }
        });
      }

      this.formData = {
        limit: data.limit,
        settlement_fee: data.settlement_fee,
        onboarding_fee: data.onboarding_fee,
        currency: data.currency || this.formData.currency,
        eligible_businesses: eligibleBusinesses,
        account_iban: data.bank_details?.account_iban || "",
        account_holder_name: data.bank_details?.account_holder_name || "",
        trn: data.additional_details?.trn || "",
        onboarding_invoice_send: data.additional_details?.onboarding_invoice_send || false,
        financing_emails_enabled: data.additional_details?.financing_emails_enabled !== undefined 
          ? data.additional_details.financing_emails_enabled 
          : true,
        is_active: data.is_active !== undefined ? data.is_active : this.formData.is_active,
        minimum_disbursement_amount: {
          limit: data.additional_details?.minimum_disbursement_amount?.limit || null,
          type: data.additional_details?.minimum_disbursement_amount?.type || "int"
        },
        billing_contact: data.billing_contact || "",
        invoice_prefix: data.invoice_prefix || "",
        kema_iban: data.bank_details?.kema_bank_details?.IBAN || ""
      };
      
      // Parse existing disbursed_at if available
      const existingDisbursed = data.additional_details?.disbursed_at;
      if (existingDisbursed) {
        try {
          // Extract just the date part (YYYY-MM-DD) from formats like "2026-01-04 00:00:00+0400"
          const dateMatch = existingDisbursed.match(/^(\d{4}-\d{2}-\d{2})/);
          if (dateMatch) {
            const dateStr = dateMatch[1]; // e.g., "2026-01-04"
            // Create a simple date object from YYYY-MM-DD
            const [year, month, day] = dateStr.split('-').map(Number);
            this.disbursedAtDate = new Date(year, month - 1, day);
          } else {
            this.disbursedAtDate = new Date();
          }
        } catch (error) {
          this.disbursedAtDate = new Date();
        }
      } else {
        this.disbursedAtDate = new Date();
      }
      
      this.payerConfigs = payerConfigs;
      this.originalData = {
        ...this.formData,
        eligible_businesses: JSON.parse(JSON.stringify(eligibleBusinesses)),
        original_bank_details: data.bank_details || {},
        original_additional_details: data.additional_details || {},
        original_payer_configs: JSON.parse(JSON.stringify(payerConfigs))
      };
    },

    async loadInitialBusinesses() {
      try {
        const response = await CompaniesService.getCustomersForBusiness({
          business_id: this.businessId,
          page: 1,
          page_size: 10
        });

        this.availableBusinesses = (response.data || []).map(customer => ({
          id: customer.id,
          name: customer.business_name || `Business ${customer.id}`,
          type: 'customer'
        }));

        this.hasMoreBusinesses = !!response.next;
        this.businessPage = 1;
      } catch (err) {
        console.error('Failed to load businesses:', err);
      }
    },

    async loadInitialGroups() {
      try {
        const response = await FinancingService.getCustomerGroups({
          business_id: this.businessId,
          page: 1,
          page_size: 20
        });

        this.availableGroups = (response.data || []).map((g) =>
          this.mapCustomerGroupListRow(g)
        );

        // Cache member IDs when the list payload includes a customers array
        (response.data || []).forEach((g) => {
          const customers = Array.isArray(g.customers) ? g.customers : [];
          if (customers.length > 0) {
            this.$set(this.groupMembers, g.id, customers);
          }
        });

        this.hasMoreGroups = !!response.next;
        this.groupPage = 1;
      } catch (err) {
        console.error('Failed to load groups:', err);
      }
    },


    resetToNewData() {
      this.hasExistingData = false;
      this.creditLineId = null;
      this.formData = {
        ...this.formData,
        limit: null,
        settlement_fee: null,
        onboarding_fee: null,
        eligible_businesses: {},
        account_iban: "",
        account_holder_name: "",
        trn: "",
        onboarding_invoice_send: true,
        financing_emails_enabled: true,
        is_active: true,
        minimum_disbursement_amount: { limit: null, type: "int" },
        billing_contact: "",
        invoice_prefix: "",
        kema_iban: ""
      };
      this.payerConfigs = {};
      this.originalData = null;
      this.selectedPayers = [];
      this.disbursedAtDate = new Date();
    },

    getPayerConfig(payer) {
      // Accept either payer object or payerKey string
      const payerKey = typeof payer === 'string' ? payer : `${payer.type}:${payer.id}`;
      
      if (!this.payerConfigs[payerKey]) {
        this.$set(this.payerConfigs, payerKey, {
          transaction_fees: {},
          transaction_fees_order: [],
          limit_allowed: 30,
          advance_rate: 100
        });
      }
      return this.payerConfigs[payerKey];
    },

    getPayerTransactionFees(payer) {
      const config = this.getPayerConfig(payer);
      const tf = config.transaction_fees || {};
      let order = config.transaction_fees_order;
      if (!Array.isArray(order) || order.length === 0) {
        order = Object.keys(tf).filter(d => d && d !== 'null').map(Number);
        this.$set(config, 'transaction_fees_order', order);
      }
      const fees = order.map(day => ({
        day,
        percentage: parseFloat(tf[day] ?? tf[String(day)] ?? 0)
      })).filter(f => !isNaN(f.day));
      return fees.length ? fees : [{ day: null, percentage: null }];
    },

    addPayerTransactionFee(payer) {
      const config = this.getPayerConfig(payer);
      const existingFees = this.getPayerTransactionFees(payer);
      const existingDays = existingFees.map(fee => fee.day).filter(day => day !== null);
      const nextDay = existingDays.length ? Math.max(...existingDays) + 30 : 30;
      
      this.$set(config.transaction_fees, nextDay, 1);
      if (!Array.isArray(config.transaction_fees_order)) {
        this.$set(config, 'transaction_fees_order', []);
      }
      config.transaction_fees_order.push(nextDay);
    },

    removePayerTransactionFee(payer, feeIndex) {
      const config = this.getPayerConfig(payer);
      const fees = this.getPayerTransactionFees(payer);
      if (fees.length > 1 && feeIndex < fees.length) {
        const day = fees[feeIndex].day;
        this.$delete(config.transaction_fees, day);
        const order = config.transaction_fees_order || [];
        const idx = order.indexOf(day);
        if (idx >= 0) order.splice(idx, 1);
      }
    },

    updatePayerTransactionFees(payer) {
      const config = this.getPayerConfig(payer);
      const fees = this.getPayerTransactionFees(payer);
      const newTf = {};
      const newOrder = [];
      fees.forEach(f => {
        if (f.day > 0 && f.percentage !== null) {
          newTf[f.day] = f.percentage;
          newOrder.push(f.day);
        }
      });
      this.$set(config, 'transaction_fees', newTf);
      this.$set(config, 'transaction_fees_order', newOrder);
    },

    updateFeeField(payer, feeIndex, field, value) {
      const config = this.getPayerConfig(payer);
      const fees = this.getPayerTransactionFees(payer);
      if (!fees[feeIndex]) return;
      
      const fee = fees[feeIndex];
      const isPlaceholder = fee.day === null;
      const order = config.transaction_fees_order || [];

      if (field === 'day' && value > 0) {
        const dayVal = parseInt(value);
        const pctVal = fee.percentage ?? 1;
        if (isPlaceholder) {
          this.$set(config.transaction_fees, dayVal, pctVal);
          order.push(dayVal);
        } else {
          const oldDay = fee.day;
          this.$delete(config.transaction_fees, oldDay);
          this.$set(config.transaction_fees, dayVal, pctVal);
          const idx = order.indexOf(oldDay);
          if (idx >= 0) order[idx] = dayVal;
        }
      } else if (field === 'percentage' && value >= 0 && fee.day) {
        this.$set(config.transaction_fees, fee.day, parseFloat(value));
      }
    },


    async loadBusinesses(reset = false) {
      if (reset) {
        this.businessPage = 1;
        this.availableBusinesses = [];
      }

      this.loadingBusinesses = true;
      try {
        const params = {
          business_id: this.businessId,
          page: this.businessPage,
          page_size: 20,
          list_view: true,
          ...(this.businessSearchTerm && { search: this.businessSearchTerm })
        };

        const response = await CompaniesService.getCustomersForBusiness(params);
        const businesses = (response.data || []).map(customer => ({
          id: customer.id,
          name: customer.business_name || `Business ${customer.id}`,
          type: 'customer'
        }));
        
        this.availableBusinesses = reset ? businesses : [...this.availableBusinesses, ...businesses];
        this.hasMoreBusinesses = !!response.next;
      } catch (err) {
        console.error('Failed to load businesses:', err);
        if (reset) this.availableBusinesses = [];
        this.hasMoreBusinesses = false;
      } finally {
        this.loadingBusinesses = false;
      }
    },

    async loadGroups(reset = false) {
      if (reset) {
        this.groupPage = 1;
        this.availableGroups = [];
      }

      this.loadingGroups = true;
      try {
        const params = {
          business_id: this.businessId,
          page: this.groupPage,
          page_size: 20,
          ...(this.groupSearchTerm && { search: this.groupSearchTerm })
        };

        const response = await FinancingService.getCustomerGroups(params);
        const groups = (response.data || []).map((g) =>
          this.mapCustomerGroupListRow(g)
        );

        (response.data || []).forEach((g) => {
          const customers = Array.isArray(g.customers) ? g.customers : [];
          if (customers.length > 0) {
            this.$set(this.groupMembers, g.id, customers);
          }
        });
        
        this.availableGroups = reset ? groups : [...this.availableGroups, ...groups];
        this.hasMoreGroups = !!response.next;
      } catch (err) {
        console.error('Failed to load groups:', err);
        if (reset) this.availableGroups = [];
        this.hasMoreGroups = false;
      } finally {
        this.loadingGroups = false;
      }
    },

    async searchBusinesses() {
      clearTimeout(this.businessSearchDebounce);
      
      if (this.businessSearchTerm.length >= 2 || this.businessSearchTerm.length === 0) {
        this.showBusinessDropdown = true;
        this.businessSearchDebounce = setTimeout(() => {
          this.loadBusinesses(true);
        }, 300);
      } else {
        this.showBusinessDropdown = false;
      }
    },

    async searchGroups() {
      clearTimeout(this.groupSearchDebounce);
      
      if (this.groupSearchTerm.length >= 2 || this.groupSearchTerm.length === 0) {
        this.showGroupDropdown = true;
        this.groupSearchDebounce = setTimeout(() => {
          this.loadGroups(true);
        }, 300);
      } else {
        this.showGroupDropdown = false;
      }
    },

    handleSearchBlur() {
      setTimeout(() => {
        if (!this.isInteractingWithDropdown) {
          this.showBusinessDropdown = false;
        }
      }, 150);
    },

    handleGroupSearchBlur() {
      setTimeout(() => {
        if (!this.isInteractingWithGroupDropdown) {
          this.showGroupDropdown = false;
        }
      }, 150);
    },

    handleDropdownScroll(event) {
      const { scrollTop, scrollHeight, clientHeight } = event.target;
      if (scrollTop + clientHeight >= scrollHeight - 20 && this.hasMoreBusinesses && !this.isLoadingMore) {
        this.isLoadingMore = true;
        this.businessPage += 1;
        this.loadBusinesses(false).finally(() => {
          this.isLoadingMore = false;
        });
      }
    },

    handleGroupDropdownScroll(event) {
      const { scrollTop, scrollHeight, clientHeight } = event.target;
      if (scrollTop + clientHeight >= scrollHeight - 20 && this.hasMoreGroups && !this.isLoadingMoreGroups) {
        this.isLoadingMoreGroups = true;
        this.groupPage += 1;
        this.loadGroups(false).finally(() => {
          this.isLoadingMoreGroups = false;
        });
      }
    },

    handleClickOutside(event) {
      const searchInput = this.$refs.searchInput;
      const dropdownMenu = this.$refs.businessDropdown;
      const groupSearchInput = this.$refs.groupSearchInput;
      const groupDropdownMenu = this.$refs.groupDropdown;
      
      // Check if click is outside both the search input and dropdown menu
      const isOutsideInput = !searchInput?.contains(event.target);
      const isOutsideDropdown = !dropdownMenu?.contains(event.target);
      const isOutsideGroupInput = !groupSearchInput?.contains(event.target);
      const isOutsideGroupDropdown = !groupDropdownMenu?.contains(event.target);
      
      if (isOutsideInput && isOutsideDropdown) {
        this.showBusinessDropdown = false;
      }
      
      if (isOutsideGroupInput && isOutsideGroupDropdown) {
        this.showGroupDropdown = false;
      }
    },

    syncModalBodyOverflow() {
      document.body.style.overflow =
        this.showGroupModal || this.showDeleteGroupModal ? "hidden" : "";
    },

    handleKeydown(event) {
      if (event.key === 'Escape') {
        if (this.showDeleteGroupModal && !this.deletingGroup) {
          this.closeDeleteGroupModal();
          return;
        }
        if (this.showBusinessDropdown) {
          this.showBusinessDropdown = false;
          this.$refs.searchInput?.focus();
        }
        if (this.showGroupDropdown) {
          this.showGroupDropdown = false;
          this.$refs.groupSearchInput?.focus();
        }
      }
    },

    isPayerSelected(payer) {
      return this.selectedPayers.some(p => p.type === payer.type && p.id === payer.id);
    },

    isCustomerInSelectedGroup(customerId) {
      const selectedGroups = this.selectedPayers.filter(p => p.type === 'group');
      for (const group of selectedGroups) {
        const members = this.groupMembers[group.id] || [];
        if (members.includes(customerId)) {
          return group.name;
        }
      }
      return null;
    },

    getGroupConflictingCustomers(groupId) {
      const members = this.groupMembers[groupId] || [];
      const selectedCustomers = this.selectedPayers.filter(p => p.type === 'customer');
      
      return selectedCustomers.filter(customer => 
        members.includes(customer.id)
      );
    },

    handlePayerClick(payer, event) {
      // Prevent all default behaviors and event propagation
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      
      // Check if this payer is disabled due to conflicts
      if (payer.type === 'customer' && this.isCustomerInSelectedGroup(payer.id)) {
        // Don't allow clicking on a customer that's in a selected group
        return false;
      }
      
      if (payer.type === 'group' && this.getGroupConflictingCustomers(payer.id).length > 0) {
        // Don't allow clicking on a group that has conflicting customers
        return false;
      }
      
      // Call the toggle method
      this.togglePayerSelection(payer);
      
      // Return false to be extra sure
      return false;
    },

    async togglePayerSelection(payer) {
      try {
        if (this.isPayerSelected(payer)) {
          this.removePayerFromSelected(payer);
        } else {
          await this.addPayerToSelected(payer);
        }
      } catch (error) {
        console.error('Error in togglePayerSelection:', error);
      }
    },

    async addPayerToSelected(payer) {
      const payerKey = `${payer.type}:${payer.id}`;
      
      // Check if already added
      if (this.isPayerSelected(payer)) {
        return;
      }
      
      // If adding a group, store its members if not already cached
      if (payer.type === 'group' && payer.customers) {
        if (!this.groupMembers[payer.id]) {
          this.$set(this.groupMembers, payer.id, payer.customers);
        }
      }
      
      // Validate before adding
      const validationResult = await this.validatePayerAddition(payer);
      if (!validationResult.valid) {
        this.error = validationResult.message;
        this.scrollToTop();
        return;
      }
      
      // Use previous payer's configuration if available
      if (this.selectedPayers.length > 0) {
        const lastPayer = this.selectedPayers[this.selectedPayers.length - 1];
        const lastKey = `${lastPayer.type}:${lastPayer.id}`;
        const lastConfig = this.payerConfigs[lastKey];
        
        if (lastConfig) {
          const lastTf = lastConfig.transaction_fees || {};
          const lastOrder = lastConfig.transaction_fees_order || [];
          this.$set(this.payerConfigs, payerKey, {
            transaction_fees: { ...lastTf },
            transaction_fees_order: [...lastOrder],
            limit_allowed: lastConfig.limit_allowed || 30,
            advance_rate: lastConfig.advance_rate || 100
          });
        } else {
          this.getPayerConfig(payerKey);
        }
      } else {
        this.getPayerConfig(payerKey);
      }
      
      this.$set(this.formData.eligible_businesses, payer.id, {
        type: payer.type,
        customer_id: payer.type === 'customer' ? payer.id : null,
        customer_group_id: payer.type === 'group' ? payer.id : null
      });
      
      this.selectedPayers.push({
        id: payer.id,
        name: payer.name,
        type: payer.type
      });
      
      // Close dropdown and clear search
      if (payer.type === 'customer') {
        this.showBusinessDropdown = false;
        this.businessSearchTerm = '';
      } else {
        this.showGroupDropdown = false;
        this.groupSearchTerm = '';
      }
    },


    async validatePayerAddition(payer) {
      // If adding a customer, check if they're in any selected group
      if (payer.type === 'customer') {
        const selectedGroups = this.selectedPayers.filter(p => p.type === 'group');
        
        for (const group of selectedGroups) {
          const members = this.groupMembers[group.id] || [];
          if (members.includes(payer.id)) {
            return {
              valid: false,
              message: `Cannot add "${payer.name}" individually because they are already part of the selected group "${group.name}".`
            };
          }
        }
      }
      
      // If adding a group, check if any of its members are already selected individually
      if (payer.type === 'group') {
        const members = this.groupMembers[payer.id] || [];
        
        if (members.length === 0) {
          // If we don't have members cached, it might be a newly created group or data issue
          console.warn(`Group ${payer.id} has no members cached`);
        }
        
        const selectedCustomers = this.selectedPayers.filter(p => p.type === 'customer');
        const conflictingCustomers = selectedCustomers.filter(customer => 
          members.includes(customer.id)
        );
        
        if (conflictingCustomers.length > 0) {
          const customerNames = conflictingCustomers.map(c => `"${c.name}"`).join(', ');
          return {
            valid: false,
            message: `Cannot add group "${payer.name}" because the following customer(s) are already added individually: ${customerNames}. Please remove them first.`
          };
        }
      }
      
      return { valid: true };
    },

    removePayerFromSelected(payer) {
      const payerKey = `${payer.type}:${payer.id}`;
      this.$delete(this.payerConfigs, payerKey);
      this.$delete(this.formData.eligible_businesses, payer.id);
      this.selectedPayers = this.selectedPayers.filter(
        p => !(p.type === payer.type && p.id === payer.id)
      );
      
      // Clean up group members cache if removing a group
      if (payer.type === 'group') {
        this.$delete(this.groupMembers, payer.id);
      }
    },

    sanitizeInvoicePrefix() {
      // Remove all special characters, only allow alphanumeric
      if (this.formData.invoice_prefix) {
        this.formData.invoice_prefix = this.formData.invoice_prefix.replace(/[^a-zA-Z0-9]/g, '');
      }
    },

    validateForm() {
      this.validationErrors = {};
      const errors = this.validationErrors;
      
      // Basic validations
      if (!this.formData.limit || this.formData.limit <= 0) {
        errors.limit = "Credit limit must be greater than 0";
      }
      if (this.formData.settlement_fee < 0) {
        errors.settlement_fee = "Settlement fees must be 0 or more";
      }
      if (this.formData.onboarding_fee < 0) {
        errors.onboarding_fee = "Onboarding fees must be 0 or more";
      }
      if (!this.formData.account_iban) {
        errors.account_iban = "Bank Account IBAN is required";
      }
      if (!this.formData.account_holder_name) {
        errors.account_holder_name = "Bank Account Holder Name is required";
      }
      if (!this.formData.minimum_disbursement_amount.limit || this.formData.minimum_disbursement_amount.limit <= 0) {
        errors.minimum_disbursement_limit = "Minimum disbursement amount must be greater than 0";
      }
      if (!this.formData.billing_contact || !this.formData.billing_contact.trim()) {
        errors.billing_contact = "Billing Contact is required";
      }

      // Payer validations
      this.selectedPayers.forEach(payer => {
        const config = this.getPayerConfig(payer);
        const fees = this.getPayerTransactionFees(payer);
        
        if (!config.limit_allowed || config.limit_allowed <= 0 || config.limit_allowed > 100) {
          errors[`payer_${payer.type}_${payer.id}_limit`] = "Limit percentage must be between 1-100%";
        }
        
        if (!config.advance_rate || config.advance_rate <= 0 || config.advance_rate > 100) {
          errors[`payer_${payer.type}_${payer.id}_advance_rate`] = "Advance rate must be between 1-100%";
        }
        
        const hasValidFee = fees.some(fee => fee.day > 0 && fee.percentage >= 0);
        if (!hasValidFee) {
          errors[`payer_${payer.type}_${payer.id}_fees`] = "At least one valid transaction fee is required";
        }
      });

      return !Object.keys(errors).length;
    },

    getUpdatedFields() {
      if (!this.hasExistingData || !this.originalData) {
        return { ...this.formData };
      }
      
      const updated = { currency: this.formData.currency };
      const fields = ['limit','settlement_fee','onboarding_fee','account_iban','account_holder_name','trn','financing_emails_enabled','is_active','billing_contact','invoice_prefix','kema_iban'];
      
      fields.forEach(field => {
        if (this.formData[field] !== this.originalData[field]) {
          updated[field] = this.formData[field];
        }
      });
      
      if (JSON.stringify(this.formData.minimum_disbursement_amount) !== JSON.stringify(this.originalData.minimum_disbursement_amount)) {
        updated.minimum_disbursement_amount = this.formData.minimum_disbursement_amount;
      }
      
      if (JSON.stringify(this.formData.eligible_businesses) !== JSON.stringify(this.originalData.eligible_businesses)) {
        updated.eligible_businesses = this.formData.eligible_businesses;
      }
      
      return updated;
    },

    async submit() {
      this.error = null;
      this.successMessage = null;
      
      // Update payer configs before validation
      this.selectedPayers.forEach(payer => {
        this.updatePayerTransactionFees(payer);
      });
      
      if (!this.validateForm()) {
        this.error = "Please fix the validation errors below.";
        this.scrollToTop();
        return;
      }
      
      this.saving = true;
      try {
        const payload = this.buildPayload();
        
        if (this.creditLineId) {
          await FinancingService.updateFinancing(this.creditLineId, payload);
          
          // Save financing rules if modified
          if (this.rulesModified) {
            await this.saveFinancingRules();
          }
          
          this.successMessage = 'Credit line updated successfully!';
          this.$showAlert('Credit line updated successfully!', 'success');
        } else {
          const response = await FinancingService.createFinancing(payload);
          
          // Set the credit line ID for rules saving
          this.creditLineId = response.data?.id;
          this.hasExistingData = true;
          
          // Save financing rules if user has selected any rules
          if (this.creditLineId && this.hasAssignedRules()) {
            await this.saveFinancingRules(true);
          }
          
          const assignedRulesCount = this.availableRules.filter(rule => rule.assigned).length;
          let successMsg = 'Credit line created successfully!';
          if (assignedRulesCount > 0) {
            successMsg += ` ${assignedRulesCount} financing rule(s) have been applied.`;
          }
          
          this.successMessage = successMsg;
          this.$showAlert(successMsg, 'success');
          
          setTimeout(() => {
            this.$router.push({
              name: 'business-insights',
              params: { business_id: this.businessId }
            });
          }, 2000);
        }
        this.scrollToTop();
      } catch (err) {
        this.error = this.formatApiError(err);
        this.scrollToTop();
      } finally {
        this.saving = false;
      }
    },

    scrollToTop() {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },

    buildPayload() {
      const payload = { 
        business_id: this.businessId,
        limit: this.formData.limit,
        settlement_fee: this.formData.settlement_fee,
        onboarding_fee: this.formData.onboarding_fee,
        currency: this.formData.currency,
        is_active: this.formData.is_active,
        eligible_businesses: {
          add: [],
          update: [],
          delete: []
        }
      };
      
      // Build lists for add/update
      this.selectedPayers.forEach(payer => {
        const config = this.getPayerConfig(payer);
        
        // Pass transaction_fees through unchanged - backend format unaffected by display order
        const tf = config.transaction_fees || {};
        const transaction_fees = typeof tf === 'object' && !Array.isArray(tf) ? { ...tf } : {};
        const payerData = {
          customer_id: payer.type === 'customer' ? payer.id : null,
          customer_group_id: payer.type === 'group' ? payer.id : null,
          transaction_fees,
          limit_allowed: config.limit_allowed,
          advance_rate: config.advance_rate || 100,
          additional_details: {
            transaction_fees,
            limit_allowed: config.limit_allowed,
            advance_rate: config.advance_rate || 100
          }
        };
        
        // If has junction ID, it's an UPDATE
        if (payer.junctionId) {
          payload.eligible_businesses.update.push({
            id: payer.junctionId,
            ...payerData
          });
        } 
        // No junction ID means it's a new ADD
        else {
          payload.eligible_businesses.add.push(payerData);
        }
      });
      
      // Track deletions - compare original vs current
      if (this.hasExistingData && this.originalData?.eligible_businesses) {
        const currentKeys = this.selectedPayers.map(p => p.id);
        const originalKeys = Object.keys(this.originalData.eligible_businesses);
        
        originalKeys.forEach(key => {
          if (!currentKeys.includes(key)) {
            const originalBusiness = this.originalData.eligible_businesses[key];
            if (originalBusiness.id) {
              payload.eligible_businesses.delete.push(originalBusiness.id);
            }
          }
        });
      }
      
      // Bank details
      if (this.formData.account_iban && this.formData.account_holder_name) {
        payload.bank_details = {
          ...(this.originalData?.original_bank_details || {}),
          account_iban: this.formData.account_iban,
          account_holder_name: this.formData.account_holder_name
        };
      }
      
      // Additional details
      payload.additional_details = {
        ...(this.originalData?.original_additional_details || {}),
        minimum_disbursement_amount: this.formData.minimum_disbursement_amount,
        trn: this.formData.trn,
        financing_emails_enabled: this.formData.financing_emails_enabled
      };
      // Top-level billing_contact (moved outside additional_details)
      payload.billing_contact = this.formData.billing_contact;
      
      // Top-level invoice_prefix
      if (this.formData.invoice_prefix) {
        payload.invoice_prefix = this.formData.invoice_prefix;
      }
      
      // Top-level kema_iban
      if (this.formData.kema_iban) {
        payload.kema_iban = this.formData.kema_iban;
      }
      
      // Format disbursed_at if date provided: %Y-%m-%d %H:%M:%S%z (with 00:00:00 time in UTC)
      if (this.disbursedAtDate) {
        const date = this.disbursedAtDate;
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        
        // Always use UTC timezone (+0000)
        payload.additional_details.disbursed_at = `${year}-${month}-${day} 00:00:00+0000`;
      }
      
      if (!this.hasExistingData) {
        payload.additional_details.onboarding_invoice_send = this.formData.onboarding_invoice_send;
      }
      
      return payload;
    },

    formatApiError(err) {
      let message = 'An error occurred while saving';
      
      if (err.response?.data) {
        const data = err.response.data;
        if (typeof data === 'string') {
          message = data;
        } else if (data.message || data.error || data.detail) {
          message = data.message || data.error || data.detail;
        }
        
        if (err.response.status) {
          message += ` (Status: ${err.response.status})`;
        }
      } else if (err.message) {
        message = err.message;
      }
      
      return formatErrorMessage(message);
    },

    goBack() {
      this.$router.go(-1);
    },

    // ================
    // CUSTOMER GROUP MODAL (create + edit)
    // ================
    mergeGroupModalCustomerLists(availRows, extras) {
      const byId = new Map();
      (availRows || []).forEach((c) => {
        byId.set(String(c.id), { id: String(c.id), name: c.name });
      });
      (extras || []).forEach((m) => {
        const id = String(m.id);
        if (!byId.has(id)) {
          byId.set(id, {
            id,
            name: m.name || `Customer ${id}`,
          });
        }
      });
      return Array.from(byId.values()).sort((a, b) =>
        (a.name || "").localeCompare(b.name || "")
      );
    },

    mapCustomerGroupListRow(apiGroup) {
      const customers = Array.isArray(apiGroup.customers)
        ? apiGroup.customers
        : [];
      const count =
        typeof apiGroup.customer_count === "number"
          ? apiGroup.customer_count
          : customers.length;
      return {
        id: apiGroup.id,
        name: apiGroup.name,
        type: "group",
        isActive: !!apiGroup.is_active,
        customerCount: count,
        customers,
      };
    },

    normalizeEmailList(val) {
      if (val == null || val === "") return [];
      if (Array.isArray(val)) {
        return val.map((s) => String(s).trim()).filter(Boolean);
      }
      return String(val)
        .split(/[,;\s]+/)
        .map((s) => s.trim())
        .filter(Boolean);
    },

    uniqAppendEmail(list, raw) {
      const t = (raw || "").trim();
      if (!t || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(t)) return list;
      if (list.includes(t)) return list;
      return [...list, t];
    },

    normalizeCustomersFieldFromApi(field) {
      if (!field || !field.length) {
        return { ids: [], meta: [] };
      }
      const first = field[0];
      if (typeof first === "object") {
        const meta = field.map((c) => ({
          id: String(c.id ?? c.customer_id),
          name:
            c.business_name ||
            c.name ||
            c.customer_name ||
            null,
        }));
        const ids = meta.map((m) => m.id);
        return { ids, meta };
      }
      const ids = field.map((id) => String(id));
      return {
        ids,
        meta: ids.map((id) => ({ id, name: null })),
      };
    },

    parseWorkflowRowsFromEmailConfigList(payload) {
      const entity = FinancingService.unwrapEntity(payload) || payload || {};
      const rows = FinancingService.unwrapListPayload(payload);
      const list =
        rows.length > 0
          ? rows
          : FinancingService.unwrapListPayload(entity);
      const mapped = list
        .map((w) => ({
          id: String(w.workflow_id ?? w.id ?? ""),
          name: String(
            w.workflow_name ??
              w.reminder_workflow ??
              w.name ??
              "Reminder workflow"
          ),
        }))
        .filter((w) => w.id);
      const seen = new Map();
      mapped.forEach((w) => {
        if (!seen.has(w.id)) seen.set(w.id, w);
      });
      return Array.from(seen.values());
    },

    parseWorkflowAssignmentId(payload, workflowOptions) {
      const entity = FinancingService.unwrapEntity(payload) || payload || {};
      const cfg =
        entity.customer_email_config ||
        entity.assignment ||
        entity;
      const widRaw =
        cfg &&
        (cfg.workflow_id ??
          cfg.workflowId ??
          (typeof cfg.workflow === "object" && cfg.workflow
            ? cfg.workflow.id ?? cfg.workflow.workflow_id
            : null));
      if (widRaw == null || widRaw === "") {
        return "";
      }
      const wid = String(widRaw);
      if (workflowOptions.some((w) => w.id === wid)) return wid;
      return wid;
    },

    workflowLabelForUnknownId(workflowId) {
      const wId = String(workflowId || "");
      if (!wId) return "";
      const short = wId.slice(0, 8);
      return `Workflow (${short}…)`;
    },

    applyDefaultReminderWorkflowSelection() {
      if (!this.canAssignCustomerGroupWorkflow) return;
      const opts = this.groupModalWorkflowOptions;
      if (!opts.length) {
        this.groupForm.workflowId = "";
        return;
      }
      const def = opts.find(
        (o) => String(o.name).toLowerCase() === "default"
      );
      this.groupForm.workflowId = String((def || opts[0]).id);
    },

    resetGroupModalFormSkeleton() {
      this.groupModalEmailDraftTo = "";
      this.groupModalEmailDraftCc = "";
      this.groupForm = {
        name: "",
        contactName: "",
        selectedCustomers: [],
        toEmails: [],
        ccEmails: [],
        workflowId: "",
        combineStatements: false,
        isActive: true,
      };
      this.groupModalWorkflowOptions = [];
      this.groupModalMemberExtras = [];
      this.groupModalFetchedCustomers = [];
      this.groupModalCustomerSearchTerm = "";
      this.groupModalCustomerPage = 1;
      this.hasMoreGroupModalCustomers = false;
      this.isLoadingMoreGroupModalCustomers = false;
    },

    async openCreateGroupModal() {
      this.groupModalMode = "create";
      this.groupModalEditingId = null;
      this.resetGroupModalFormSkeleton();
      this.groupValidationErrors = {};
      this.showGroupModal = true;
      await this.bootstrapGroupModal();
    },

    async openEditGroupModal(group) {
      this.groupModalMode = "edit";
      this.groupModalEditingId = group.id;
      this.resetGroupModalFormSkeleton();
      this.groupValidationErrors = {};
      this.showGroupModal = true;
      await this.bootstrapGroupModal();
    },

    closeGroupModal() {
      if (this.savingGroup) return;
      this.showGroupModal = false;
      this.resetGroupModalFormSkeleton();
      this.groupValidationErrors = {};
      this.groupModalEditingId = null;
      clearTimeout(this.groupModalCustomerDebounce);
    },

    async bootstrapGroupModal() {
      this.groupModalInitialLoading = true;
      this.groupValidationErrors = {};
      try {
        let groupPayload = null;
        const loaders = [];

        loaders.push(() => this.loadGroupModalCustomersPage(true));

        if (this.canAssignCustomerGroupWorkflow) {
          loaders.push(async () => {
            const raw = await FinancingService.getEmailConfigList(
              this.businessId
            );
            let opts = this.parseWorkflowRowsFromEmailConfigList(raw);
            if (this.groupModalMode === "edit" && this.groupModalEditingId) {
              let assignRaw;
              try {
                assignRaw = await FinancingService.getEmailConfigAssignment(
                  this.businessId,
                  this.groupModalEditingId
                );
              } catch (e) {
                assignRaw = null;
              }
              const wid = this.parseWorkflowAssignmentId(assignRaw, opts);
              if (
                wid &&
                !opts.find((o) => String(o.id) === String(wid))
              ) {
                opts = [
                  ...opts,
                  {
                    id: String(wid),
                    name: this.workflowLabelForUnknownId(wid),
                  },
                ];
              }
              this.groupModalWorkflowOptions = opts;
              this.groupForm.workflowId = wid ? String(wid) : "";
            } else {
              this.groupModalWorkflowOptions = opts;
            }
          });
        }

        if (this.groupModalMode === "edit" && this.groupModalEditingId) {
          loaders.push(async () => {
            const raw = await FinancingService.getCustomerGroup(
              this.groupModalEditingId,
              this.businessId
            );
            groupPayload = FinancingService.unwrapEntity(raw);
          });
        }

        await Promise.all(loaders.map((fn) => fn()));

        if (groupPayload) {
          const g = groupPayload;
          this.groupForm.name = g.name || "";
          this.groupForm.contactName = g.contact_name || g.contactName || "";
          this.groupForm.toEmails = this.normalizeEmailList(
            g.to_emails ?? g.toEmails
          );
          this.groupForm.ccEmails = this.normalizeEmailList(
            g.cc_emails ?? g.ccEmails
          );
          this.groupForm.combineStatements = !!(
            g.combine_customer_statements ??
            g.combine_customer_statement ??
            g.combineStatements
          );
          this.groupForm.isActive = !!g.is_active;
          const { ids, meta } = this.normalizeCustomersFieldFromApi(
            g.customers || g.customer_ids || []
          );
          this.groupModalMemberExtras = meta;
          this.groupForm.selectedCustomers = [...ids];
        } else {
          this.applyDefaultReminderWorkflowSelection();
        }
      } catch (err) {
        console.error("Failed to open group modal:", err);
        this.groupValidationErrors.general = this.formatApiError(err);
        this.$showAlert(this.groupValidationErrors.general, "error");
      } finally {
        this.groupModalInitialLoading = false;
      }
    },

    validateGroupForm() {
      this.groupValidationErrors = {};
      if (!this.groupForm.name || !this.groupForm.name.trim()) {
        this.groupValidationErrors.name = "Group name is required";
      }
      return Object.keys(this.groupValidationErrors).length === 0;
    },

    buildGroupSaveBody() {
      return {
        name: this.groupForm.name.trim(),
        contact_name: (this.groupForm.contactName || "").trim() || undefined,
        customers: this.groupForm.selectedCustomers.map(String),
        to_emails: this.groupForm.toEmails,
        cc_emails: this.groupForm.ccEmails,
        combine_customer_statements: this.groupForm.combineStatements,
        is_active: this.groupForm.isActive,
      };
    },

    async toggleGroupActiveFromModal() {
      if (
        this.groupModalMode !== "edit" ||
        !this.groupModalEditingId ||
        this.togglingGroupActive
      ) {
        return;
      }
      const next = !this.groupForm.isActive;
      this.togglingGroupActive = true;
      try {
        await FinancingService.updateCustomerGroup(
          this.groupModalEditingId,
          this.businessId,
          { is_active: next }
        );
        this.groupForm.isActive = next;
        this.successMessage = next
          ? "Customer group is now active."
          : "Customer group is now inactive.";
        this.$showAlert(this.successMessage, "success");
        await this.loadGroups(true);
        await this.loadInitialGroups();
      } catch (err) {
        const errorMsg = this.formatApiError(err);
        this.groupValidationErrors.general = errorMsg;
        this.$showAlert(errorMsg, "error");
      } finally {
        this.togglingGroupActive = false;
      }
    },

    async maybePostWorkflowAssignment(groupId) {
      if (!this.canAssignCustomerGroupWorkflow || !groupId) return;
      const wid = this.groupForm.workflowId;
      if (wid === null || wid === undefined || String(wid).trim() === "") {
        return;
      }
      await FinancingService.postEmailConfigAssignment(this.businessId, {
        group_id: String(groupId),
        workflow_id: String(wid),
      });
    },

    async submitGroupModal() {
      if (!this.validateGroupForm()) return;
      this.savingGroup = true;
      try {
        const body = this.buildGroupSaveBody();
        let savedName = this.groupForm.name.trim();
        let savedId = this.groupModalEditingId;

        if (this.groupModalMode === "create") {
          const response = await FinancingService.createCustomerGroup(
            this.businessId,
            body
          );
          const entity = FinancingService.unwrapEntity(response) || response;
          savedId = entity && (entity.id || entity.uuid);
          savedName = (entity && entity.name) || savedName;
          if (!savedId) {
            throw new Error("Create response did not include a group id");
          }
        } else {
          const response = await FinancingService.updateCustomerGroup(
            this.groupModalEditingId,
            this.businessId,
            body
          );
          const entity = FinancingService.unwrapEntity(response) || response;
          savedName = (entity && entity.name) || savedName;
        }

        await this.maybePostWorkflowAssignment(savedId);

        const n = this.groupForm.selectedCustomers.length;
        this.successMessage =
          this.groupModalMode === "create"
            ? `Customer group "${savedName}" created successfully with ${n} customer(s).`
            : `Customer group "${savedName}" updated successfully with ${n} customer(s).`;
        this.$showAlert(this.successMessage, "success");

        await this.loadGroups(true);
        await this.loadInitialGroups();

        if (savedId && this.groupMembers[savedId] !== undefined) {
          this.$set(
            this.groupMembers,
            savedId,
            [...this.groupForm.selectedCustomers]
          );
        }

        this.savingGroup = false;
        this.closeGroupModal();
      } catch (err) {
        const errorMsg = this.formatApiError(err);
        this.groupValidationErrors.general = errorMsg;
        this.$showAlert(errorMsg, "error");
      } finally {
        this.savingGroup = false;
      }
    },

    async loadGroupModalCustomersPage(reset = false) {
      if (reset) {
        this.groupModalCustomerPage = 1;
        this.groupModalFetchedCustomers = [];
      }
      this.loadingGroupModalCustomers = true;
      try {
        const params = {
          page: this.groupModalCustomerPage,
          page_size: 20,
        };
        if (this.groupModalCustomerSearchTerm) {
          params.search = this.groupModalCustomerSearchTerm;
        }
        const response = await FinancingService.getAvailableCustomersForGroups(
          this.businessId,
          params
        );
        const rows = FinancingService.unwrapListPayload(response).map(
          (customer) => ({
            id: customer.id,
            name:
              customer.business_name ||
              customer.name ||
              `Business ${customer.id}`,
          })
        );

        let combined;
        if (reset) {
          combined = rows;
        } else {
          const byId = new Map(
            [...this.groupModalFetchedCustomers, ...rows].map((c) => [
              String(c.id),
              c,
            ])
          );
          combined = Array.from(byId.values());
        }
        this.groupModalFetchedCustomers = combined;
        this.hasMoreGroupModalCustomers = !!(response && response.next);
      } catch (err) {
        console.error("Failed to load available customers for group:", err);
        if (reset) this.groupModalFetchedCustomers = [];
        this.hasMoreGroupModalCustomers = false;
      } finally {
        this.loadingGroupModalCustomers = false;
      }
    },

    toggleGroupModalCustomer(customerId) {
      const cid = String(customerId);
      const idx = this.groupForm.selectedCustomers.findIndex(
        (id) => String(id) === cid
      );
      if (idx > -1) {
        this.groupForm.selectedCustomers.splice(idx, 1);
      } else {
        this.groupForm.selectedCustomers.push(customerId);
      }
    },

    getGroupModalHiddenCustomersCount() {
      return this.groupModalCustomerOptions.filter((customer) =>
        this.isGroupModalCustomerInConflict(customer.id)
      ).length;
    },

    clearGroupFormCustomers() {
      this.groupForm.selectedCustomers = [];
    },

    searchGroupModalCustomers() {
      clearTimeout(this.groupModalCustomerDebounce);
      if (
        this.groupModalCustomerSearchTerm.length >= 2 ||
        this.groupModalCustomerSearchTerm.length === 0
      ) {
        this.groupModalCustomerDebounce = setTimeout(() => {
          this.loadGroupModalCustomersPage(true);
        }, 300);
      }
    },

    handleGroupModalCustomerScroll(event) {
      const { scrollTop, scrollHeight, clientHeight } = event.target;
      if (
        scrollTop + clientHeight >= scrollHeight - 20 &&
        this.hasMoreGroupModalCustomers &&
        !this.isLoadingMoreGroupModalCustomers
      ) {
        this.isLoadingMoreGroupModalCustomers = true;
        this.groupModalCustomerPage += 1;
        this.loadGroupModalCustomersPage(false).finally(() => {
          this.isLoadingMoreGroupModalCustomers = false;
        });
      }
    },

    isGroupModalCustomerSelected(customerId) {
      return this.groupForm.selectedCustomers.some(
        (id) => String(id) === String(customerId)
      );
    },

    groupModalConflictContextIds() {
      const individuallyAddedCustomerIds = this.selectedPayers
        .filter((p) => p.type === "customer")
        .map((p) => p.id);

      const editingGroupId =
        this.groupModalMode === "edit" ? this.groupModalEditingId : null;
      const selectedGroups = this.selectedPayers.filter(
        (p) => p.type === "group" && p.id !== editingGroupId
      );
      const customersInSelectedGroups = [];
      selectedGroups.forEach((group) => {
        const members = this.groupMembers[group.id] || [];
        customersInSelectedGroups.push(...members.map(String));
      });
      return { individuallyAddedCustomerIds, customersInSelectedGroups };
    },

    isGroupModalCustomerInConflict(customerId) {
      const cid = String(customerId);
      const { individuallyAddedCustomerIds, customersInSelectedGroups } =
        this.groupModalConflictContextIds();
      return (
        individuallyAddedCustomerIds.map(String).includes(cid) ||
        customersInSelectedGroups.includes(cid)
      );
    },

    removeEmailChip(section, index) {
      if (section === "to") {
        this.groupForm.toEmails.splice(index, 1);
      } else {
        this.groupForm.ccEmails.splice(index, 1);
      }
    },

    commitEmailDraft(section) {
      const raw =
        section === "to" ? this.groupModalEmailDraftTo : this.groupModalEmailDraftCc;
      const trimmed = (raw || "").trim();
      if (!trimmed) return;
      if (trimmed.includes(",") || trimmed.includes(";")) {
        const parts = trimmed.split(/[,;]+/).map((p) => p.trim()).filter(Boolean);
        if (section === "to") {
          let next = this.groupForm.toEmails;
          parts.forEach((p) => {
            next = this.uniqAppendEmail(next, p);
          });
          this.groupForm.toEmails = next;
          this.groupModalEmailDraftTo = "";
        } else {
          let next = this.groupForm.ccEmails;
          parts.forEach((p) => {
            next = this.uniqAppendEmail(next, p);
          });
          this.groupForm.ccEmails = next;
          this.groupModalEmailDraftCc = "";
        }
        return;
      }
      if (section === "to") {
        const next = this.uniqAppendEmail(this.groupForm.toEmails, trimmed);
        if (next.length !== this.groupForm.toEmails.length) {
          this.groupModalEmailDraftTo = "";
        }
        this.groupForm.toEmails = next;
      } else {
        const next = this.uniqAppendEmail(this.groupForm.ccEmails, trimmed);
        if (next.length !== this.groupForm.ccEmails.length) {
          this.groupModalEmailDraftCc = "";
        }
        this.groupForm.ccEmails = next;
      }
    },

    onEmailDraftKeydown(event, section) {
      if (event.key === "," || event.key === ";") {
        event.preventDefault();
        this.commitEmailDraft(section);
      }
    },

    openDeleteGroupModal(group) {
      this.groupPendingDelete = { id: group.id, name: group.name };
      const isGroupInUse = this.selectedPayers.some(
        (p) => p.type === "group" && p.id === group.id
      );
      this.deleteGroupModalBlockedReason = isGroupInUse
        ? `Cannot delete group "${group.name}" because it is currently attached to Eligible Payers. Please remove it from Eligible Payers first.`
        : null;
      this.showDeleteGroupModal = true;
    },

    closeDeleteGroupModal(force = false) {
      if (this.deletingGroup && !force) return;
      this.showDeleteGroupModal = false;
      this.groupPendingDelete = null;
      this.deleteGroupModalBlockedReason = null;
    },

    async confirmDeleteGroup() {
      if (
        !this.groupPendingDelete ||
        this.deleteGroupModalBlockedReason ||
        this.deletingGroup
      ) {
        return;
      }
      const group = this.groupPendingDelete;
      this.deletingGroup = true;
      try {
        await FinancingService.deleteCustomerGroup(group.id, this.businessId);

        this.successMessage = `Customer group "${group.name}" deleted successfully!`;
        this.$showAlert(
          `Customer group "${group.name}" deleted successfully!`,
          "success"
        );

        await this.loadGroups(true);
        await this.loadInitialGroups();

        this.closeDeleteGroupModal(true);
      } catch (err) {
        const errorMsg = this.formatApiError(err);
        this.error = errorMsg;
        this.$showAlert(errorMsg, "error");
        this.scrollToTop();
      } finally {
        this.deletingGroup = false;
      }
    },

    setupAutoHideAlerts() {
      // Clear any existing timeouts
      if (this.alertTimeouts) {
        Object.values(this.alertTimeouts).forEach(clearTimeout);
      }
      this.alertTimeouts = {};
    },

    autoHideAlert(type) {
      // Clear existing timeout for this alert type
      if (this.alertTimeouts[type]) {
        clearTimeout(this.alertTimeouts[type]);
      }
      
      // Set new timeout to hide alert after 5 seconds
      this.alertTimeouts[type] = setTimeout(() => {
        if (type === 'error') {
          this.error = null;
        } else if (type === 'success') {
          this.successMessage = null;
        }
      }, 5000);
    },

    // ================
    // FINANCING RULES METHODS
    // ================
    async loadFinancingRules() {      
      this.loadingRules = true;
      try {
        // Only load rules if we have a credit line ID
        if (this.creditLineId) {
          const response = await RulesService.getAccountRules(this.creditLineId);
          this.availableRules = (response.data || []).map(rule => ({
            id: rule.id,
            name: rule.name,
            threshold: rule.threshold,
            assigned: rule.assigned
          }));
        } else {
          // For new credit lines, load all available rules without assignments
          const response = await RulesService.getAccountRules(null);
          this.availableRules = (response.data || []).map(rule => ({
            id: rule.id,
            name: rule.name,
            threshold: rule.threshold || 0.5,
            assigned: false
          }));
        }
        
        // Store original data for comparison
        this.originalRulesData = JSON.parse(JSON.stringify(this.availableRules));
        this.rulesModified = false;
      } catch (err) {
        console.error('Failed to load financing rules:', err);
        this.availableRules = [];
      } finally {
        this.loadingRules = false;
      }
    },

    onRuleAssignmentChange(rule) {
      this.checkRulesModified();
    },

    onRuleThresholdChange(rule) {
      // Ensure threshold is within valid range
      if (rule.threshold < 0) rule.threshold = 0;
      if (rule.threshold > 1) rule.threshold = 1;
      
      this.checkRulesModified();
    },

    checkRulesModified() {
      this.rulesModified = JSON.stringify(this.availableRules) !== JSON.stringify(this.originalRulesData);
    },

    hasAssignedRules() {
      return this.availableRules.some(rule => rule.assigned);
    },

    async saveFinancingRules(forceUpdate = false) {
      if (!this.creditLineId) return;
      
      // Save rules if they are modified OR if we're forcing an update OR if there are assigned rules
      const shouldSave = this.rulesModified || forceUpdate || this.hasAssignedRules();
      if (!shouldSave) return;
      
      try {
        const rulesData = this.availableRules.map(rule => ({
          id: rule.id,
          threshold: rule.threshold,
          assigned: rule.assigned
        })).filter(rule => rule.assigned);
        
        // Only call the API if there are rules to save
        if (rulesData.length > 0) {
          await RulesService.updateAccountRules(this.creditLineId, rulesData);
        }
        
        // Update original data to reflect saved state
        this.originalRulesData = JSON.parse(JSON.stringify(this.availableRules));
        this.rulesModified = false;
        
        return true;
      } catch (err) {
        console.error('Failed to save financing rules:', err);
        throw err;
      }
    }
  }
};
</script>

<style scoped lang="scss">
@import "./FinancingSettings.scss";
</style>